/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Button, DetailTimelineRow, Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import type { DealEvent } from '@/services/requests/housien-services/DealsService';
import { formatLocalizedDateTime } from '@/shared/utils/date';
import { DEAL_PANEL_CLASS, DealPanelLabel } from './DealPanel';
import { daysSince } from '../utils/deal-state';
import { useDealStageLabels, type DealStage } from '../constants/deal-stages';
import { timelineDotColor, toTimelineEntries, type TimelineEntry } from '../utils/deal-events';
import {
  ACCENT_BG_CLASS,
  ACCENT_RING_CLASS,
  ACCENT_SOFT_BG_CLASS,
  ACCENT_TEXT_CLASS,
  formatDealMoney,
  type DealAccent,
} from '../utils/deal-state';

interface DealTimelineSidebarProps {
  events: DealEvent[];
  /** Opens the contact form from the header and the near-empty nudge. */
  onRecordContact?: () => void;
}

/**
 * Localized stage-name resolver for the `from`/`to` pair on stage moves.
 */
function useStageNamer(): (stage: string | undefined) => string {
  const labels = useDealStageLabels();
  return (stage) => (stage ? labels[stage as DealStage] ?? stage : '—');
}

/**
 * The detail line under an entry's title: the broker's own words for a
 * recorded contact, the stage transition for a move, the amount for a
 * proposal, nothing otherwise.
 *
 * `event.stage` is deliberately never read — the backend declares it on
 * the wire but never populates it, so rendering it would only ever
 * produce an empty line. `event.note` is the exception: only
 * `contact_recorded` populates it, and this is that entry's whole point.
 */
function useEntryDetail(entry: TimelineEntry): string | null {
  const { t } = useTranslation('features/deals');
  const nameOf = useStageNamer();
  const { event } = entry;
  if (event.type === 'contact_recorded') return event.note ?? null;
  if (event.type === 'proposal_recorded') return formatDealMoney(event.value);
  if (!isCompleteStageChange(event)) return null;
  return t('detail.timeline.transition', { from: nameOf(event.from), to: nameOf(event.to) });
}

/**
 * A stage change carrying both ends of the move. The backend has been seen
 * to declare the event without populating `from`/`to`, and half a transition
 * renders as a sentence with a hole in it.
 */
function isCompleteStageChange(
  event: TimelineEntry['event'],
): event is TimelineEntry['event'] & { from: string; to: string } {
  return event.type === 'stage_changed' && Boolean(event.from) && Boolean(event.to);
}

/**
 * One timeline entry: dot, title, detail, the automatic-move sub-line,
 * and the timestamp with its attribution badge.
 *
 * Exported so any panel showing a filtered slice of the same event feed
 * (e.g. the Registros tab's contact-only view) renders identically to
 * the full timeline instead of growing its own competing row style.
 */
export function TimelineEntryRow({
  entry,
  accent,
  isLast,
}: {
  entry: TimelineEntry;
  accent: DealAccent;
  isLast?: boolean;
}) {
  const { t } = useTranslation('features/deals');
  const nameOf = useStageNamer();
  const detail = useEntryDetail(entry);
  const isSystem = entry.actor === null;
  // The dot reads the event, the attribution pill reads the screen: what
  // happened is a property of the entry, who is responsible for it is a
  // property of the attendance.
  const dot = timelineDotColor(entry.event);
  const pillColour = isSystem ? undefined : `var(--accent-${accent})`;

  return (
    <DetailTimelineRow
      isLast={isLast}
      item={{
        id: `${entry.event.timestamp}-${entry.event.type}`,
        title: t(entry.titleKey),
        detail,
        accent: dot,
        ringed: true,
        meta: (
          <Text component="time" className="tabular-nums">
            {formatLocalizedDateTime(entry.event.timestamp)}
          </Text>
        ),
        pill: {
          label: isSystem ? t('detail.timeline.automatic') : (entry.actor as string),
          accent: pillColour,
        },
      }}
      /* The stage move a trigger performed as a side effect of a human fact
       * is folded into that fact's row rather than standing as a second
       * entry that would read as a second human action. It is the one line
       * the shared row has no concept for, so it goes through the slot. */
      extra={
        entry.autoMove ? (
          <Text
            component="span"
            className="text-[length:var(--text-tag)] italic text-(--text-tertiary-soft)"
          >
            {t('detail.timeline.autoMove', {
              from: nameOf(entry.autoMove.from),
              to: nameOf(entry.autoMove.to),
            })}
          </Text>
        ) : undefined
      }
    />
  );
}

/**
 * Empty state for a deal whose history has not started.
 */
function TimelineEmpty() {
  const { t } = useTranslation('features/deals');
  return (
    <div data-testid="deal-timeline-empty" className="flex flex-col items-start gap-(--space-2) py-(--space-4)">
      <div className="flex w-full flex-col gap-(--space-2) opacity-50">
        <div className="h-2 w-1/2 rounded-full bg-(--surface-tertiary)" />
        <div className="h-2 w-3/4 rounded-full bg-(--surface-tertiary)" />
      </div>
      <Text component="span" variant="small" c="var(--text-secondary)" className="mt-(--space-2)">
        {t('detail.timeline.empty')}
      </Text>
      <Text component="span" variant="meta" c="var(--text-tertiary-soft)" className="leading-normal">
        {t('detail.timeline.emptyHint')}
      </Text>
    </div>
  );
}

/**
 * How long ago an event happened, in whole days.
 *
 * The rail states "há 6 dias" rather than a date because what a reader
 * wants off it is the spacing between events, not their calendar
 * positions. The exact instant is still there, on hover.
 *
 * @param timestamp - The event's instant.
 * @param t - The deals-namespace translation function.
 * @returns The localized age.
 */
export function ageOf(timestamp: string, t: (key: string, params?: Record<string, unknown>) => string): string {
  const days = daysSince(timestamp);
  if (days == null) return '';
  if (days === 0) return t('detail.timeline.today');
  return t('detail.timeline.daysAgo', { count: days });
}

/**
 * One entry on the activity rail: a dot on a hairline, what happened,
 * who is behind it, and how long ago.
 */
function ActivityRow({ entry, isLast }: { entry: TimelineEntry; isLast: boolean }) {
  const { t } = useTranslation('features/deals');
  const nameOf = useStageNamer();
  const detail = useEntryDetail(entry);
  const actor = entry.actor ?? t('detail.timeline.automatic');
  const meta = [actor, detail].filter(Boolean).join(' · ');

  return (
    <div className="flex min-w-0 gap-(--space-2-5)" data-testid="deal-activity-row">
      <div className="flex w-[12px] flex-none flex-col items-center">
        <Text component="span"
          className="mt-[5px] h-[9px] w-[9px] flex-none rounded-(--radius-full)"
          style={{ background: timelineDotColor(entry.event) }}
         />
        <Text component="span" className={cn('w-px flex-1 bg-(--border-subtle)', isLast && 'opacity-0')}  />
      </div>
      <div className="flex min-w-0 flex-col gap-[2px] pb-(--space-3-5)">
        <Text
          component="span"
          variant="bodySm"
          c="var(--text-primary)"
          className="[font-weight:var(--font-semibold)] leading-[var(--leading-snug)]"
        >
          {t(entry.titleKey)}
        </Text>
        {meta && (
          <Text
            component="span"
            variant="meta"
            c="var(--text-secondary)"
            className="leading-[var(--leading-normal)]"
          >
            {meta}
          </Text>
        )}
        {/* The stage move a trigger performed as a side effect of a human
            fact stays folded into that fact rather than standing as a
            second entry that would read as a second human action. */}
        {entry.autoMove && (
          <Text
            component="span"
            variant="meta"
            c="var(--text-tertiary-soft)"
            className="italic"
          >
            {t('detail.timeline.autoMove', {
              from: nameOf(entry.autoMove.from),
              to: nameOf(entry.autoMove.to),
            })}
          </Text>
        )}
        <Text
          component="time"
          title={formatLocalizedDateTime(entry.event.timestamp)}
          variant="meta"
          c="var(--text-tertiary-soft)"
        >
          {ageOf(entry.event.timestamp, t)}
        </Text>
      </div>
    </div>
  );
}

/**
 * The activity rail beside the attendance: everything that happened to
 * it, newest first.
 *
 * Human facts are credited to the person who recorded them; events the
 * backend writes on its own are credited to "Automático", so the rail
 * never presents a trigger's work as somebody's. The stage move a
 * trigger performed as a side effect of a human fact is folded into that
 * fact rather than standing as a second entry.
 *
 * @param props.events - The deal's raw event list, any order.
 * @param props.onRecordContact - Opens the contact form from the header.
 * @returns The activity panel.
 */
export function DealTimelineSidebar({ events, onRecordContact }: DealTimelineSidebarProps) {
  const { t } = useTranslation('features/deals');
  const entries = toTimelineEntries(events);
  // A deal whose only event is its own creation is not empty, but it has
  // nothing to read either. Saying so — and offering the one action that
  // starts the history — beats a list with a single self-referential row.
  const onlyCreation = entries.length === 1 && entries[0].event.type === 'created';

  return (
    <div className={cn(DEAL_PANEL_CLASS, 'gap-(--space-3)')} data-testid="deal-activity-panel">
      <DealPanelLabel
        accessory={onRecordContact ? (
          <UnstyledButton
            type="button"
            onClick={onRecordContact}
            data-testid="activity-record-contact"
            className="whitespace-nowrap border-none bg-transparent p-0 [font-weight:var(--font-semibold)] text-(--seed-base) cursor-pointer"
            style={{ fontSize: 'var(--text-meta)' }}
          >
            {t('detail.timeline.record')}
          </UnstyledButton>
        ) : undefined}
      >
        {t('detail.timeline.title')}
      </DealPanelLabel>

      {entries.length === 0 ? (
        <TimelineEmpty />
      ) : (
        <div role="list" data-testid="deal-timeline" className="flex flex-col">
          {entries.map((entry, index) => (
            <ActivityRow
              key={`${entry.event.timestamp}-${entry.event.type}`}
              entry={entry}
              isLast={index === entries.length - 1}
            />
          ))}
        </div>
      )}

      {onlyCreation && onRecordContact && (
        <div className="flex flex-col items-start gap-(--space-2)">
          <Text
            component="span"
            variant="meta"
            c="var(--text-secondary)"
            className="leading-normal"
          >
            {t('detail.timeline.creationOnly')}
          </Text>
          <Button size="sm" variant="outlined" onClick={onRecordContact}>
            {t('detail.actions.recordContact')}
          </Button>
        </div>
      )}
    </div>
  );
}
